import {
  FileText,
  LayoutDashboard,
  Settings,
  Sparkles,
  Tag,
  Users,
  Wrench,
} from 'lucide-react';
import { NavLink } from 'react-router-dom';
import { InvoyssLogo } from '@/features/brand/InvoyssLogo';
import { cn } from '@/lib/utils';
import { OrgSwitcher } from './OrgSwitcher';

interface SidebarProps {
  onNavigate?: () => void;
}

interface NavItemDef {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  exact?: boolean;
}

const PRIMARY: NavItemDef[] = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, exact: true },
  { to: '/invoices', label: 'Invoices', icon: FileText },
  { to: '/clients', label: 'Clients', icon: Users },
];

const LIBRARY: NavItemDef[] = [
  { to: '/services', label: 'Services', icon: Wrench },
  { to: '/taxes', label: 'Tax options', icon: Tag },
];

const TOOLS: NavItemDef[] = [
  { to: '/ai', label: 'AI assistant', icon: Sparkles },
  { to: '/settings/account', label: 'Settings', icon: Settings },
];

export function Sidebar({ onNavigate }: SidebarProps) {
  return <SidebarContent onNavigate={onNavigate} />;
}

export function SidebarContent({ onNavigate }: SidebarProps) {
  return (
    <div className="flex h-full flex-col gap-2">
      <div className="flex items-center gap-2 px-5 pb-2 pt-5">
        <InvoyssLogo size="sm" />
      </div>

      <div className="px-3">
        <OrgSwitcher />
      </div>

      <nav className="scrollbar-thin mt-2 flex-1 overflow-y-auto px-3 pb-4 pt-2">
        <SectionLabel>Workspace</SectionLabel>
        <NavGroup items={PRIMARY} onNavigate={onNavigate} />
        <SectionLabel className="mt-5">Library</SectionLabel>
        <NavGroup items={LIBRARY} onNavigate={onNavigate} />
        <SectionLabel className="mt-5">Tools</SectionLabel>
        <NavGroup items={TOOLS} onNavigate={onNavigate} />
      </nav>

      <div className="border-t border-line px-5 py-3 text-[11px] text-muted-foreground">
        Invoyss · Alpha
      </div>
    </div>
  );
}

function NavGroup({
  items,
  onNavigate,
}: {
  items: NavItemDef[];
  onNavigate?: () => void;
}) {
  return (
    <ul className="flex flex-col gap-1">
      {items.map((item) => (
        <li key={item.to}>
          <NavLink
            to={item.to}
            end={item.exact}
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                'group flex items-center gap-3 rounded-md px-2.5 py-2 text-sm font-medium transition-colors',
                isActive
                  ? 'bg-secondary text-foreground'
                  : 'text-muted-foreground hover:bg-secondary/60 hover:text-foreground',
              )
            }
          >
            <item.icon className="size-4 shrink-0" />
            <span className="truncate">{item.label}</span>
          </NavLink>
        </li>
      ))}
    </ul>
  );
}

function SectionLabel({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <p
      className={cn(
        'mb-1 px-2.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground',
        className,
      )}
    >
      {children}
    </p>
  );
}
